import React, { useState } from 'react';
import { BACKEND_URL } from '../config';
import { motion, AnimatePresence } from 'motion/react';
import {
  CheckCircle,
  CircleNotch,
  DownloadSimple,
  Envelope,
  Shield,
  WarningCircle,
  X
} from '@phosphor-icons/react';

const EbookDownloadModal = ({ isOpen, onClose, ebook }) => {
  const [email, setEmail] = useState('');
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [downloadUrl, setDownloadUrl] = useState(null);

  const handleDownloadClick = () => {
    if (downloadUrl) {
      window.open(downloadUrl, '_blank');
    }
  };

  const handleAssessmentClick = () => {
    // Close modal and redirect to registration
    handleClose();
    window.location.href = '/register';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${BACKEND_URL}/api/ebook/email-gate`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            ebook_id: ebook.id,
            email: email,
            marketing_opt_in: marketingOptIn
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to process request');
      }

      const data = await response.json();
      setDownloadUrl(`${BACKEND_URL}${data.download_url}`);
      setSuccess(true);

      // Auto-download after 1 second
      setTimeout(() => {
        window.location.href = `${BACKEND_URL}${data.download_url}`;
      }, 1000);

    } catch (err) {
      console.error('Download request failed:', err);
      setError(err.message || 'Failed to process your request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setEmail('');
    setMarketingOptIn(false);
    setSuccess(false);
    setError(null);
    setDownloadUrl(null);
    onClose();
  };

  if (!isOpen || !ebook) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleClose}
          className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        />

        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="relative z-10 glass rounded-3xl p-8 max-w-md w-full card-glow"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close Button */}
          <button
            type="button"
            onClick={handleClose}
            className="absolute top-6 right-6 p-2 rounded-full glass hover:bg-[rgba(255,255,255,0.1)] transition-colors z-20"
          >
            <X size={24} className="text-[#A0AEC0]" />
          </button>

          {!success ? (
            <>
              {/* Header */}
              <div className="mb-6">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#6366F1]/20 to-[#EC4899]/20 flex items-center justify-center mx-auto mb-4">
                  <DownloadSimple size={32} className="text-[#6366F1]" />
                </div>
                <h2 className="text-2xl font-bold text-white text-center mb-2 select-none">
                  Get the Free Guide
                </h2>
                <p className="text-[#A0AEC0] text-center text-sm select-none">
                  Enter your email to receive instant access to this guide.
                </p>
              </div>

              {/* Error Message */}
              {error && (
                <div className="mb-4 p-4 rounded-xl bg-red-500/10 border border-red-500/30 flex items-start gap-3">
                  <WarningCircle size={20} className="text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-red-400 text-sm">{error}</p>
                </div>
              )}

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#A0AEC0] mb-2">
                    Email *
                  </label>
                  <div className="relative">
                    <Envelope size={20} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-[#718096]" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      placeholder="you@example.com"
                      className="w-full pl-12 pr-4 py-3 rounded-xl glass text-white bg-[#1A1B23] border border-[rgba(255,255,255,0.1)] focus:border-[#6366F1] outline-none transition-colors"
                    />
                  </div>
                </div>

                {/* Marketing Opt-In - NEVER pre-checked */}
                <div className="p-4 rounded-xl bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.1)]">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={marketingOptIn}
                      onChange={(e) => setMarketingOptIn(e.target.checked)}
                      className="mt-1 w-5 h-5 rounded border-2 border-[#6366F1] bg-transparent checked:bg-[#6366F1] cursor-pointer"
                    />
                    <span className="text-sm text-[#A0AEC0] leading-relaxed">
                      I'd like to receive occasional insights and updates from Man & Machine
                    </span>
                  </label>
                </div>

                {/* Privacy Text */}
                <div className="p-4 rounded-xl bg-[rgba(20,241,149,0.05)] border border-[rgba(20,241,149,0.2)] flex items-start gap-3">
                  <Shield size={18} className="text-[#14F195] flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-[#A0AEC0] leading-relaxed">
                    We respect your privacy. Your email is stored securely and never shared. You can unsubscribe at any time.
                  </p>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading || !email}
                  className="w-full relative overflow-hidden rounded-full group disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-[#14F195] to-[#6366F1]"></div>
                  <span className="relative z-10 flex items-center justify-center gap-2 py-3 text-white font-bold">
                    {loading ? (
                      <>
                        <CircleNotch size={20} className="animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <DownloadSimple size={20} />
                        Send Me the Guide
                      </>
                    )}
                  </span>
                </button>
              </form>
            </>
          ) : (
            <>
              {/* Success State */}
              <div className="text-center py-6">
                <div className="w-20 h-20 rounded-full bg-[#14F195]/20 flex items-center justify-center mx-auto mb-6">
                  <CheckCircle size={48} className="text-[#14F195]" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-3 select-none">
                  DownloadSimple Ready!
                </h2>
                <p className="text-[#A0AEC0] mb-2 select-none">
                  Your download should start automatically.
                </p>
                <p className="text-[#718096] text-sm mb-6 select-none">
                  We've also sent a download link to your email.
                </p>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDownloadClick();
                  }}
                  className="relative z-20 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-[#14F195] to-[#6366F1] text-white font-bold hover:opacity-90 transition-opacity cursor-pointer"
                >
                  <DownloadSimple size={20} />
                  DownloadSimple Now
                </button>
                
                {/* Next Step CTA */}
                <div className="mt-8 pt-6 border-t border-[rgba(255,255,255,0.1)]">
                  <p className="text-white font-semibold mb-2 select-none">
                    Want to see where AI could deliver ROI in your business?
                  </p>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAssessmentClick();
                    }}
                    className="relative z-20 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#6366F1] text-white font-semibold hover:bg-[#5558E3] transition-colors cursor-pointer"
                  >
                    Start a Free AI Opportunity Assessment
                    <svg 
                      className="w-4 h-4" 
                      fill="none" 
                      viewBox="0 0 24 24" 
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
                
                <button
                  onClick={handleClose}
                  className="block w-full mt-4 px-6 py-3 rounded-full glass hover:bg-[rgba(255,255,255,0.1)] text-white font-semibold transition-all"
                >
                  Close
                </button>
              </div>
            </>
          )}
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default EbookDownloadModal;
